<!-- eslint-disable @typescript-eslint/no-explicit-any -->
<script setup lang="ts">
import { calculateValidity, stringToFaDateTime } from '@payjib/shared'
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import {
  getAllUsersAction,
  getBuyerPaymentAction,
  getTransactionDetailsAction,
  printTransactionAction,
  registerSettlementReceiptAutoAction,
} from '@/api/functions/transactions/get-transaction-details.action'
import { getTransactionTransfersAction } from '@/api/functions/transactions/get-transaction-transfers.action.ts'
import { updateTransactionStatusAction } from '@/api/functions/transactions/transaction-status.action'
import type { User } from '@/api/typings/user.typing.ts'
import { useMutationApi, useQueryApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { apiFileUrl, paymentUrl } from '@/configs/urls'
import { getErrorMessage } from '@/utils/errorUtils'
import { numberWithCommas } from '@/utils/number-with-commas'
import DetailRow from './partials/DetailRow.vue'
import SectionHeader from './partials/SectionHeader.vue'
import TransactionFileUpload from './partials/TransactionFileUpload.vue'
import TransactionNotes from './partials/TransactionNotes.vue'
import TransactionTransferHistory from './partials/TransactionTransferHistory.vue'
import TransferTransactionModal from './partials/TransferTransactionModal.vue'
import UpdateTransactionModal from './partials/UpdateTransactionModal.vue'
import SellerSettlementModal from './transaction-status/SellerSettlementModal.vue'
import TransactionStatusWrapper from './transaction-status/TransactionStatusWrapper.vue'

const route = useRoute()
const toast = useToast()
const transactionId = computed(() => route.params.id as string)

const showSellerSettlementModal = ref(false)
const showCancelModal = ref(false)
const showSettlementModal = ref(false)
const showEditModal = ref(false)
const showTransferModal = ref(false)
const usersPage = 1
const copied = ref(false)
const notesRef = ref()
const isOpeningEditModal = ref(false)
const isOpeningTransferModal = ref(false)
const activePartyTab = ref<'buyer' | 'seller'>('buyer')
const activeReceiptTab = ref<'buyerReceipt' | 'sellerReceipt' | 'settlementReceipt'>('buyerReceipt')

const { data: transactionResponse, refetch: refetchTransaction } = useQueryApi({
  queryKey: ['transactionDetails', transactionId],
  queryFn: () => getTransactionDetailsAction(transactionId.value),
  staleTime: 1000 * 60 * 5,
  refetchInterval: 2 * 60 * 1000,
  refetchOnWindowFocus: 'always',
})

const { data: transactionTransfersResponse, refetch: refetchTransactionTransfers } = useQueryApi({
  queryKey: ['transactionTransfers', transactionId],
  queryFn: () => getTransactionTransfersAction(transactionId.value),
  enabled: computed(() => !!transactionId.value),
  staleTime: 1000 * 60 * 5,
  refetchInterval: 2 * 60 * 1000,
  refetchOnWindowFocus: 'always',
})

const { data: buyerPaymentResponse } = useQueryApi({
  queryKey: ['buyerPayment', transactionId],
  queryFn: () => getBuyerPaymentAction(transactionId.value),
  enabled: computed(() => !!transactionId.value),
  staleTime: 1000 * 60 * 5,
})

const { data: usersResponse, refetch: refetchUsers } = useQueryApi({
  queryKey: ['users', usersPage],
  queryFn: () => getAllUsersAction(),
  staleTime: 1000 * 60 * 5,
})

function toSelectOptions(arr: Array<{ id: number; uuid: string; fullName: string }>) {
  return (
    arr?.map((user) => ({
      label: `\u2068${user.id}\u2069 | \u2068${user.fullName}\u2069`,
      value: user.uuid,
    })) ?? []
  )
}

const transactionTransfer = computed(() => transactionTransfersResponse.value?.data?.[0])

const transferredFrom = computed(
  () =>
    transactionTransfersResponse.value?.data.find((transfer) => transfer.toTransaction.id === transactionId.value)
      ?.fromTransaction
)

const transferredTo = computed(
  () =>
    transactionTransfersResponse.value?.data.find((transfer) => transfer.fromTransaction.id === transactionId.value)
      ?.toTransaction
)

const usersSelectOptions = computed(() => toSelectOptions(usersResponse.value?.data ?? []))

const buyerPayment = computed(() => buyerPaymentResponse.value?.data)
const transaction = computed(() => transactionResponse.value?.data)

const isTransferred = computed(() => transaction.value?.status === 'TRANSFERRED')

const status = computed(() => {
  const status = transaction?.value?.status

  switch (status) {
    case 'INITIAL_OWNER_CONFIRMATION_PENDING':
    case 'BUYER_PAYMENT_PENDING':
    case 'BUYER_PAYMENT_VERIFICATION_PENDING':
    case 'SELLER_PAYMENT_PENDING':
    case 'SELLER_PAYMENT_VERIFICATION_PENDING':
    case 'SETTLE_WITH_SELLER_PENDING':
      return {
        label: 'در حال انجام',
        class: 'pending',
      }

    case 'CANCELLED':
      return {
        label: 'لغو شده',
        class: 'canceled',
      }

    case 'TRANSFERRED':
      return {
        label: 'منتقل شده',
        class: 'transferred',
      }

    case 'BUYER_REJECTED':
    case 'SELLER_REJECTED':
      return {
        label: status === 'BUYER_REJECTED' ? ' عدم پذیرش توسط خریدار' : ' عدم پذیرش توسط فروشنده',
        class: 'rejected',
      }

    case 'FINISHED':
      return {
        label: 'موفق',
        class: 'success',
      }

    default:
      return {
        label: 'موفق',
        class: 'success',
      }
  }
})

const updateStatusMutation = useMutationApi({
  mutationFn: (newStatus: string) => {
    if (transaction.value?.uuid) {
      return updateTransactionStatusAction(transaction.value.uuid, { status: newStatus })
    }

    throw Error('the Id not provided')
  },

  onSuccess: () => {
    toast.success('تراکنش با موفقیت لغو شد.')
    reloadData()
  },

  onError: (err: unknown) => {
    toast.error(getErrorMessage(err))
  },
})

const handleCancelOffer = async () => {
  updateStatusMutation.mutate('CANCELLED')
}

const settlementMutation = useMutationApi({
  mutationFn: () => {
    if (transaction.value?.uuid) return registerSettlementReceiptAutoAction(transaction.value.uuid)
    throw Error('the Id not provided')
  },
  onSuccess: () => {
    toast.success('تسویه اتوماتیک با موفقیت ثبت شد')
    reloadData()
  },
  onError: (err: any) => {
    toast.error(getErrorMessage(err))
  },
})

const handleSettlement = () => {
  settlementMutation.mutate()
}

const printTransactionMutation = useMutationApi({
  mutationFn: (uuid: string) => printTransactionAction(uuid),
  onSuccess: (res) => {
    const pdfPath = res?.pdf_path
    if (pdfPath) {
      window.open(apiFileUrl(pdfPath), '_blank')
    } else {
      toast.error('PDF یافت نشد')
    }
  },
  onError: (err: any) => {
    toast.error(getErrorMessage(err))
  },
})

const handlePrintTransaction = () => {
  if (transaction?.value?.uuid) {
    printTransactionMutation.mutate(transaction?.value?.uuid)
  } else {
    toast.error('شناسه تراکنش موجود نیست')
  }
}

const updateTransaction = async () => {
  try {
    await reloadData()
    toast.success('تراکنش با موفقیت بروزرسانی شد')
  } catch (err) {
    toast.error(getErrorMessage(err))
  }
}

const paymentLink = computed(() =>
  paymentUrl(
    `/payment?transaction_id=${transaction?.value?.uuid}&amount=${transaction?.value?.buyerPayableRialAmount}&user_id=${transaction?.value?.buyer.uuid}`
  )
)

const copyPaymentLink = async () => {
  try {
    await navigator.clipboard.writeText(paymentLink.value || '')
    copied.value = true
    toast.success('لینک پرداخت کپی شد!')
  } catch (err) {
    toast.error(getErrorMessage(err), 'خطا')
  }
}

const reloadData = async () => {
  await Promise.all([refetchTransaction(), refetchTransactionTransfers()])

  notesRef.value?.reload()
}

const openEditModal = async () => {
  if (isOpeningEditModal.value) return

  try {
    isOpeningEditModal.value = true

    if (!usersResponse.value?.data?.length) {
      await refetchUsers()
    }

    showEditModal.value = true
  } finally {
    isOpeningEditModal.value = false
  }
}

const openTransferModal = async () => {
  if (isOpeningTransferModal.value) return

  try {
    isOpeningTransferModal.value = true

    if (!usersResponse.value?.data?.length) {
      await refetchUsers()
    }

    showTransferModal.value = true
  } finally {
    isOpeningTransferModal.value = false
  }
}
</script>

<template>
  <div class="mb-24">

    <SellerSettlementModal v-model="showSellerSettlementModal" :transaction="transaction"
      @cancel="showSellerSettlementModal = false" @finish="reloadData()" />

    <ConfirmModal v-model="showCancelModal" title="لغو تراکنش" :message="'آیا از لغو این تراکنش اطمینان دارید؟'"
      confirm-text="بله، لغو شود" cancel-text="بازگشت" @confirm="handleCancelOffer" @cancel="showCancelModal = false" />


    <ConfirmModal v-model="showSettlementModal" title="تسویه اتوماتیک تراکنش"
      :message="'آیا از تسویه اتوماتیک این تراکنش اطمینان دارید؟'" cancel-text="بازگشت" @confirm="handleSettlement"
      confirm-text="بله، تسویه شود" @cancel="showSettlementModal = false" />

    <UpdateTransactionModal v-model="showEditModal" :transaction="transaction" :users="usersSelectOptions"
      @confirm="reloadData" @cancel="showEditModal = false" />

    <TransferTransactionModal v-model="showTransferModal" :transaction="transaction" :users="usersSelectOptions"
      @confirm="reloadData" @cancel="showTransferModal = false" />



    <router-link to="/transactions"
      class="mb-4 flex w-fit items-center gap-1 h-10 rounded-lg px-5 text-[13px] text-[#2d5396] hover:bg-[#D5E2F1]">
      <img src="/svg/back-icon.svg" class="me-1" width="22" />
      <span>بازگشت</span>
    </router-link>

    <!-- TOOLBAR -->
    <div
      class="sticky top-20 z-40 mb-9 flex flex-col gap-4 rounded-t-none rounded-r-none rounded-bl-xl border border-[#d8d8d8] bg-white p-4 shadow-[0_4px_16px_rgba(30,55,95,0.06)] lg:flex-row lg:items-center lg:justify-between">

      <!-- Title + badges -->
      <div class="flex flex-col gap-3 xl:flex-row xl:items-center">
        <h1 class="text-[#1b325a] text-[20px] font-bold whitespace-nowrap">
          تراکنش شماره <span class="font-fa">{{ transaction?.code }}</span>
        </h1>

        <div class="flex flex-wrap items-center gap-2">
          <div
            class="flex items-center justify-center font-semibold text-[11px] h-8 rounded-[10px] px-3"
            :class="{
              'bg-[#d5ddea] text-[#2d5396]': status.class === 'pending',
              'bg-[#e6fff0] text-[#2ed06e]': status.class === 'success',
              'bg-[#fff8e7] text-[#ffba0e]': status.class === 'canceled',
              'bg-[#fde7ef] text-[#ed145b]': status.class === 'rejected',
              'bg-[#fff4e5] text-[#b86b00]': status.class === 'transferred',
            }">
            {{ status.label }}
          </div>

          <div v-if="transaction?.discount?.currentDiscount"
            class="flex items-center bg-[#f3d0f2] text-[#8c228a] justify-center font-semibold text-[11px] h-8 rounded-[10px] px-3">
            دارای تخفیف
          </div>

          <!-- Transfer chain -->
          <div v-if="transactionTransfer"
            class="flex flex-wrap items-center gap-1.5 rounded-[10px] bg-[#eef3fb] px-3 py-1.5 text-[11px] font-medium">
            <span class="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-[#2d5396]">
              <img src="/svg/transfer2.svg" width="12" />
            </span>
            <span v-if="transferredFrom" class="text-[#6c7a94]">منتقل شده از</span>
            <RouterLink v-if="transferredFrom" :to="`/transactions/${transactionTransfer.fromTransaction.id}`"
              class="font-bold text-[#2d5396] hover:underline">
              {{ transactionTransfer.fromTransaction.code }}
            </RouterLink>
            <span v-if="transferredFrom && transferredTo" class="text-[#6c7a94]">به</span>
            <span v-if="transferredTo && !transferredFrom" class="text-[#6c7a94]">منتقل شده به</span>
            <RouterLink v-if="transferredTo" :to="`/transactions/${transactionTransfer.toTransaction.id}`"
              class="font-bold text-[#2d5396] hover:underline">
              {{ transactionTransfer.toTransaction.code }}
            </RouterLink>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex flex-wrap items-center gap-2">
        <Button v-if="transaction?.status !== 'FINISHED' && !isTransferred" :isAdmin="true"
          :loading="isOpeningEditModal" @click="openEditModal"
          class="h-10! px-4! rounded-[10px]! py-0! text-[13px] font-medium">
          <span class="flex items-center justify-center gap-2 text-white">
            <img width="18" src="/svg/edit-icon.svg" />
            {{ isOpeningEditModal ? 'در حال بارگذاری...' : 'ویرایش' }}
          </span>
        </Button>

        <Button v-if="transaction?.status !== 'FINISHED' && !isTransferred" :isAdmin="true"
          :loading="isOpeningTransferModal" @click="openTransferModal"
          class="h-10! px-4! rounded-[10px]! py-0! text-[13px] font-medium">
          <span class="flex items-center justify-center gap-2 text-white">
            <img width="18" src="/svg/transfer2.svg" />
            {{ isOpeningTransferModal ? 'در حال بارگذاری...' : 'انتقال' }}
          </span>
        </Button>

        <Button v-if="transaction?.status !== 'FINISHED' && transaction?.status !== 'CANCELLED' && !isTransferred"
          @click="showCancelModal = true" :isAdmin="true" variant="outline"
          class="h-10! px-4! rounded-[10px]! py-0! text-[13px] font-medium">
          لغو
        </Button>

        <Button :isAdmin="true" @click="updateTransaction"
          class="h-10! px-4! rounded-[10px]! py-0! text-[13px] font-medium">
          بروزرسانی
        </Button>

        <span class="mx-1 hidden h-6 w-px bg-[#eaeef5] sm:block"></span>

        <Button v-if="transaction?.status !== 'FINISHED' && !isTransferred" :isAdmin="true"
          @click="showSellerSettlementModal = true" :disabled="transaction?.status !== 'SETTLE_WITH_SELLER_PENDING'"
          variant="outline" class="h-10! px-4! rounded-[10px]! py-0! text-[13px] font-medium">
          تسویه دستی
        </Button>

        <Button v-if="transaction?.status !== 'FINISHED' && !isTransferred" :isAdmin="true"
          @click="showSettlementModal = true" :disabled="transaction?.status !== 'SETTLE_WITH_SELLER_PENDING'"
          class="h-10! px-4! rounded-[10px]! py-0! text-[13px] font-medium">
          تسویه اتوماتیک
        </Button>

        <Button v-if="transaction?.status === 'FINISHED'" @click="handlePrintTransaction" :isAdmin="true"
          class="h-10! px-4! rounded-[10px]! py-0! text-sm font-medium bg-transparent! text-[#2d5396]!">
          <span class="flex items-center justify-center gap-2">
            چاپ رسید مشتریان
            <img width="20" src="/svg/ic-requests.9ced902.svg" />
          </span>
        </Button>
      </div>

    </div>


    <!-- SUMMARY STRIP -->
    <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
      <div class="flex flex-col gap-1 rounded-xl border border-[#d8d8d8] bg-white p-4">
        <span class="text-xs text-gray-500">نوع تراکنش</span>
        <span class="font-fa text-sm font-semibold text-[#1b325a]">
          درخواست {{ transaction?.requisition?.type === 'BUY' ? 'خرید' : 'فروش' }}
        </span>
      </div>

      <div class="flex flex-col gap-1 rounded-xl border border-[#d8d8d8] bg-white p-4">
        <span class="text-xs text-gray-500">وضعیت</span>
        <span class="text-sm font-semibold"
          :class="{
            'text-[#2d5396]': status.class === 'pending',
            'text-[#2ed06e]': status.class === 'success',
            'text-[#ffba0e]': status.class === 'canceled',
            'text-[#ed145b]': status.class === 'rejected',
            'text-[#b86b00]': status.class === 'transferred',
          }">
          {{ status.label }}
        </span>
      </div>

      <div class="flex flex-col gap-1 rounded-xl border border-[#d8d8d8] bg-white p-4">
        <span class="text-xs text-gray-500">ارز</span>
        <span class="font-fa text-sm font-semibold text-[#1b325a]">{{ transaction?.currency?.titleFa || '—' }}</span>
      </div>

      <div class="flex flex-col gap-1 rounded-xl border border-[#d8d8d8] bg-white p-4">
        <span class="text-xs text-gray-500">مقدار</span>
        <span class="font-fa text-sm font-semibold text-[#1b325a]">{{ numberWithCommas(transaction?.currencyAmount) }}</span>
      </div>

      <div class="flex flex-col gap-1 rounded-xl border border-[#d8d8d8] bg-white p-4">
        <span class="text-xs text-gray-500">نرخ تبدیل</span>
        <span class="font-fa text-sm font-semibold text-[#1b325a]">{{ numberWithCommas(transaction?.exchangeRate) }}</span>
      </div>
    </div>


    <!-- STATUS + DETAILS -->
    <div class="grid lg:grid-cols-2 mt-8 gap-6 items-stretch">
      <div class="flex flex-col gap-4 h-full">
        <SectionHeader title="وضعیت انجام تراکنش" />
        <TransactionStatusWrapper :transaction="transaction" :buyerPayment="buyerPayment" @reload="reloadData()" />
      </div>

      <div class="flex flex-col gap-4 h-full">
        <SectionHeader title="جزئیات تراکنش" />
        <div class="w-full h-full flex flex-col gap-5 p-5 lg:p-6 bg-white border border-[#d8d8d8] rounded-xl">
          <DetailRow label="کد درخواست">
            <span class="font-fa">{{ transaction?.requisition?.code || '—' }}</span>
          </DetailRow>
          <DetailRow label="تاریخ ثبت">
            <span dir="ltr" class="font-fa">{{ transaction?.createdAt ? stringToFaDateTime(transaction?.createdAt) : '—' }}</span>
          </DetailRow>
          <DetailRow label="زمان آخرین تغییرات">
            <span class="font-fa">{{ transaction?.updatedAt ? calculateValidity(transaction?.updatedAt) : '—' }}</span>
          </DetailRow>
          <div v-if="transaction?.requisitionDescription"
            class="border-t border-[#eaeef5] pt-4 text-[13px] text-[#4e648c] font-medium">
            توضیحات: {{ transaction?.requisitionDescription }}
          </div>

          <div class="border-t border-[#eaeef5] pt-4 flex flex-col gap-3">
            <h4 class="text-sm font-semibold text-[#303b4f]">خلاصه مالی</h4>
            <DetailRow label="مبلغ اولیه ریالی">
              <span class="font-fa">{{ transaction?.initialRialAmount ? numberWithCommas(transaction.initialRialAmount) : 0 }} تومان</span>
            </DetailRow>
            <DetailRow label="مبلغ درآمد ارزی">
              <span class="font-fa">{{ transaction?.currencyIncomeAmount ? numberWithCommas(transaction.currencyIncomeAmount) : 0 }}</span>
            </DetailRow>
          </div>

          <div class="mt-auto flex items-center justify-between rounded-xl bg-[#f6f8fb] p-4">
            <span class="text-sm text-[#6b7280]">جمع درآمد ریالی تراکنش</span>
            <span class="font-fa text-lg font-bold text-[#1b325a]">
              {{ numberWithCommas(transaction?.finalCommissionRialAmount) }}
              <span class="text-sm font-medium">تومان</span>
            </span>
          </div>
        </div>
      </div>
    </div>


    <!-- PARTIES (TABS) -->
    <section class="mt-10">
      <div class="mb-4 inline-flex items-center gap-1 rounded-xl border border-[#d8d8d8] bg-white p-1">
        <button type="button" @click="activePartyTab = 'buyer'"
          class="flex items-center gap-2 rounded-lg px-6 py-2 text-sm font-semibold transition-colors"
          :class="activePartyTab === 'buyer' ? 'bg-[#2d5396] text-white' : 'text-[#4e648c] hover:bg-[#f2f5fa]'">
          خریدار
          <span v-if="transaction?.discounts?.buyer" class="h-2 w-2 rounded-full bg-[#8c228a]" />
        </button>
        <button type="button" @click="activePartyTab = 'seller'"
          class="flex items-center gap-2 rounded-lg px-6 py-2 text-sm font-semibold transition-colors"
          :class="activePartyTab === 'seller' ? 'bg-[#2d5396] text-white' : 'text-[#4e648c] hover:bg-[#f2f5fa]'">
          فروشنده
          <span v-if="transaction?.discounts?.seller" class="h-2 w-2 rounded-full bg-[#8c228a]" />
        </button>
      </div>

      <!-- Buyer tab -->
      <div v-show="activePartyTab === 'buyer'">
        <div class="rounded-xl border border-[#d8d8d8] bg-white p-5 lg:p-6">
          <div v-if="transaction?.discounts?.buyer"
            class="mb-4 flex w-fit items-center bg-[#d5ddea] text-[#2d5396] justify-center text-[13px] font-medium h-7 rounded-[10px] px-3">
            دارای تخفیف {{ transaction?.discounts?.buyer?.type.title_fa }}
          </div>
          <div class="grid lg:grid-cols-2 gap-8">
          <!-- اطلاعات خریدار -->
          <div class="flex flex-col gap-4">
            <h3 class="text-sm font-semibold text-[#303b4f]">اطلاعات خریدار</h3>

            <DetailRow label="نام">
              <RouterLink v-if="transaction?.buyer?.id" :to="`/users/${transaction.buyer.id}`"
                class="text-[#2d5396] hover:underline">
                {{ transaction?.buyer?.fullName }}
              </RouterLink>
              <span v-else>{{ transaction?.buyer?.fullName || '—' }}</span>
            </DetailRow>
            <DetailRow label="کد کاربری">
              <span class="font-fa">{{ transaction?.buyer?.code || '—' }}</span>
            </DetailRow>
            <DetailRow label="شماره موبایل">
              <span class="font-fa">{{ transaction?.buyer?.mobile || '—' }}</span>
            </DetailRow>
            <DetailRow label="ایمیل">
              <span>{{ transaction?.buyer?.email || '—' }}</span>
            </DetailRow>
            <DetailRow label="تعداد تراکنش های موفق">
              <span class="font-fa">{{ transaction?.buyer?.succeedTransaction }}</span>
            </DetailRow>
            <DetailRow label="نوع انتقال">
              <span v-if="transaction?.requisition?.type === 'BUY'" class="font-fa">
                {{
                  transaction?.requisition?.transferMethods?.length > 0
                    ? transaction?.requisition?.transferMethods.map(method => method.label).join('، ')
                    : 'تعیین نشده'
                }}
              </span>
              <span v-if="transaction?.requisition?.type === 'SELL'" class="font-fa">
                {{
                  transaction?.offer?.transferMethods?.length > 0
                    ? transaction?.offer?.transferMethods.map(method => method.label).join('، ')
                    : 'تعیین نشده'
                }}
              </span>
            </DetailRow>
            <DetailRow label="کشور مقصد حساب ارزی">
              <div class="flex items-center gap-1">
                <span v-if="transaction?.bankAccountCountries?.internationalBuyer">
                  {{ transaction?.bankAccountCountries?.internationalBuyer?.title?.fa }}
                </span>
                <span v-if="transaction?.bankAccountCountries?.internationalPaypalBuyer">
                  {{ transaction?.bankAccountCountries?.internationalPaypalBuyer?.title?.fa }}
                </span>
                <span v-if="transaction?.bankAccountCountries?.internationalRevolutBuyer">
                  {{ transaction?.bankAccountCountries?.internationalRevolutBuyer?.title?.fa }}
                </span>
                <span v-if="transaction?.bankAccountCountries?.internationalWiseBuyer">
                  {{ transaction?.bankAccountCountries?.internationalWiseBuyer?.title?.fa }}
                </span>
              </div>
            </DetailRow>
          </div>

          <!-- اطلاعات مالی خریدار -->
          <div class="flex flex-col gap-4">
            <h3 class="text-sm font-semibold text-[#303b4f]">اطلاعات مالی خریدار</h3>

            <DetailRow label="مبلغ پرداختی">
              <span class="font-fa">{{ numberWithCommas(transaction?.buyerPayableRialAmount) }} تومان</span>
            </DetailRow>
            <DetailRow label="کارمزد">
              <span class="font-fa">
                {{ transaction?.buyerCommissionDiscountedRialAmount ?
                  numberWithCommas(transaction?.buyerCommissionDiscountedRialAmount) : 0 }} تومان
              </span>
            </DetailRow>
            <DetailRow label="مالیات">
              <span class="font-fa">
                {{ transaction?.buyerTaxRialAmount ? numberWithCommas(transaction?.buyerTaxRialAmount) : 0 }} تومان
              </span>
            </DetailRow>
            <DetailRow label="تخفیف">
              <span>{{ transaction?.discount?.buyer ? 'دارد' : 'ندارد' }}</span>
            </DetailRow>
            <DetailRow v-if="transaction?.discount?.buyer" label="عنوان تخفیف">
              <span>{{ transaction?.discount?.buyer?.title || '—' }}</span>
            </DetailRow>
            <DetailRow v-if="transaction?.discount?.buyer" label="شماره تخفیف">
              <span class="font-fa">{{ transaction?.discount?.buyer?.id }}</span>
            </DetailRow>
            <DetailRow v-if="transaction?.discount?.buyer" label="درصد تخفیف">
              <span class="font-fa">{{ transaction?.discount?.buyer?.rate }}%</span>
            </DetailRow>

            <div v-if="paymentLink" @click="copyPaymentLink"
              class="mt-1 grid grid-cols-12 items-center gap-4 rounded-lg bg-[#f6f8fb] p-3 text-sm font-medium cursor-pointer">
              <div class="col-span-3 flex items-center gap-1 text-[#2d5396] text-nowrap">
                لینک پرداخت
                <img src="/svg/ic-invite.e61911c.svg" alt="copy-icon" />
              </div>
              <div class="col-span-9 font-fa text-[#1b325a] text-end overflow-x-hidden overflow-y-auto lg:overflow-hidden">
                {{ paymentLink }}
              </div>
            </div>
          </div>
        </div>

        <!-- حساب‌های بانکی خریدار -->
        <div class="mt-8 border-t border-[#eaeef5] pt-6">
          <h3 class="text-sm font-semibold text-[#303b4f] mb-4">حساب‌های بانکی خریدار</h3>

          <div class="grid lg:grid-cols-3 gap-4 items-start">
            <!-- buyerInternationalBank -->
            <div v-if="transaction?.bankAccounts?.buyerInternationalBank"
              class="flex flex-col gap-4 rounded-lg border border-[#eaeef5] p-4">
              <div class="text-[#1b325a] text-[15px] font-semibold">حساب بین المللی</div>
              <div class="flex flex-col gap-3">
                <DetailRow label="نام دارنده حساب">
                  <span>{{ transaction?.bankAccounts?.buyerInternationalBank.ownerFullName || '—' }}</span>
                </DetailRow>
                <DetailRow label="نام بانک">
                  <span>{{ transaction?.bankAccounts?.buyerInternationalBank.bankName || '—' }}</span>
                </DetailRow>
                <DetailRow label="شماره IBAN">
                  <span class="font-fa" dir="ltr">{{ transaction?.bankAccounts?.buyerInternationalBank.ibanNumber || '—' }}</span>
                </DetailRow>
                <DetailRow label="BIC Code">
                  <span class="font-fa" dir="ltr">{{ transaction?.bankAccounts?.buyerInternationalBank.bicNumber || '—' }}</span>
                </DetailRow>
                <DetailRow label="شماره حساب پوندی">
                  <span class="font-fa">
                    {{ transaction?.bankAccounts?.buyerInternationalBank?.bankAccountNumber || 'ندارد' }}
                  </span>
                </DetailRow>
                <DetailRow label="Sort Code">
                  <span class="font-fa">
                    {{ transaction?.bankAccounts?.buyerInternationalBank?.bankAccountSortCode || 'ندارد' }}
                  </span>
                </DetailRow>
                <DetailRow label="کشور">
                  <span>{{ transaction?.bankAccountCountries?.internationalBuyer?.title?.fa || '—' }}</span>
                </DetailRow>
              </div>
            </div>

            <!-- buyerInternationalRevolutBank -->
            <div v-if="transaction?.bankAccounts?.buyerInternationalRevolutBank"
              class="flex flex-col gap-4 rounded-lg border border-[#eaeef5] p-4">
              <div class="text-[#1b325a] text-[15px] font-semibold">حساب رولوت</div>
              <div class="flex flex-col gap-3">
                <DetailRow label="نام دارنده حساب">
                  <span>{{ transaction?.bankAccounts?.buyerInternationalRevolutBank.ownerFullName || '—' }}</span>
                </DetailRow>
                <DetailRow label="نام بانک">
                  <span>{{ transaction?.bankAccounts?.buyerInternationalRevolutBank.bankName || '—' }}</span>
                </DetailRow>
                <DetailRow label="شماره IBAN">
                  <span class="font-fa" dir="ltr">{{ transaction?.bankAccounts?.buyerInternationalRevolutBank.ibanNumber || '—' }}</span>
                </DetailRow>
                <DetailRow label="BIC Code">
                  <span class="font-fa" dir="ltr">{{ transaction?.bankAccounts?.buyerInternationalRevolutBank.bicNumber || '—' }}</span>
                </DetailRow>
                <DetailRow label="Revolut Tag">
                  <span class="font-fa" dir="ltr">{{ transaction?.bankAccounts?.buyerInternationalRevolutBank.revolutTag || '—' }}</span>
                </DetailRow>
                <DetailRow label="کشور">
                  <span>{{ transaction?.bankAccountCountries?.internationalRevolutBuyer?.title?.fa || '—' }}</span>
                </DetailRow>
              </div>
            </div>

            <!-- buyerInternationalPaypalBank -->
            <div v-if="transaction?.bankAccounts?.buyerInternationalPaypalBank"
              class="flex flex-col gap-4 rounded-lg border border-[#eaeef5] p-4">
              <div class="text-[#1b325a] text-[15px] font-semibold">حساب پی‌پال</div>
              <div class="flex flex-col gap-3">
                <DetailRow label="نام دارنده حساب">
                  <span>{{ transaction?.bankAccounts?.buyerInternationalPaypalBank.ownerFullName || '—' }}</span>
                </DetailRow>
                <DetailRow label="نام بانک">
                  <span>{{ transaction?.bankAccounts?.buyerInternationalPaypalBank.bankName || '—' }}</span>
                </DetailRow>
                <DetailRow label="Paypal Email">
                  <span dir="ltr">{{ transaction?.bankAccounts?.buyerInternationalPaypalBank.paypalEmail || '—' }}</span>
                </DetailRow>
                <DetailRow label="Paypal Id">
                  <span class="font-fa" dir="ltr">{{ transaction?.bankAccounts?.buyerInternationalPaypalBank.paypalId || '—' }}</span>
                </DetailRow>
                <DetailRow label="کشور">
                  <span>{{ transaction?.bankAccountCountries?.internationalPaypalBuyer?.title?.fa || '—' }}</span>
                </DetailRow>
              </div>
            </div>

            <!-- buyerInternationalWiseBank -->
            <div v-if="transaction?.bankAccounts?.buyerInternationalWiseBank"
              class="flex flex-col gap-4 rounded-lg border border-[#eaeef5] p-4">
              <div class="text-[#1b325a] text-[15px] font-semibold">حساب وایز</div>
              <div class="flex flex-col gap-3">
                <DetailRow label="نام دارنده حساب">
                  <span>{{ transaction?.bankAccounts?.buyerInternationalWiseBank.ownerFullName || '—' }}</span>
                </DetailRow>
                <DetailRow label="نام بانک">
                  <span>{{ transaction?.bankAccounts?.buyerInternationalWiseBank.bankName || '—' }}</span>
                </DetailRow>
                <DetailRow label="شماره IBAN">
                  <span class="font-fa" dir="ltr">{{ transaction?.bankAccounts?.buyerInternationalWiseBank.ibanNumber || '—' }}</span>
                </DetailRow>
                <DetailRow label="BIC Code">
                  <span class="font-fa" dir="ltr">{{ transaction?.bankAccounts?.buyerInternationalWiseBank.bicNumber || '—' }}</span>
                </DetailRow>
                <DetailRow label="کشور">
                  <span>{{ transaction?.bankAccountCountries?.internationalWiseBuyer?.title?.fa || '—' }}</span>
                </DetailRow>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>

      <!-- Seller tab -->
      <div v-show="activePartyTab === 'seller'">
        <div class="rounded-xl border border-[#d8d8d8] bg-white p-5 lg:p-6">
          <div v-if="transaction?.discounts?.seller"
            class="mb-4 flex w-fit items-center bg-[#d5ddea] text-[#2d5396] justify-center text-[13px] font-medium h-7 rounded-[10px] px-3">
            دارای تخفیف {{ transaction?.discounts?.seller?.type.title_fa }}
          </div>
          <div class="grid lg:grid-cols-2 gap-8">
          <!-- اطلاعات فروشنده -->
          <div class="flex flex-col gap-4">
            <h3 class="text-sm font-semibold text-[#303b4f]">اطلاعات فروشنده</h3>

            <DetailRow label="نام">
              <RouterLink v-if="transaction?.seller?.id" :to="`/users/${transaction.seller.id}`"
                class="text-[#2d5396] hover:underline">
                {{ transaction?.seller?.fullName }}
              </RouterLink>
              <span v-else>{{ transaction?.seller?.fullName || '—' }}</span>
            </DetailRow>
            <DetailRow label="کد کاربری">
              <span class="font-fa">{{ transaction?.seller?.code || '—' }}</span>
            </DetailRow>
            <DetailRow label="شماره موبایل">
              <span class="font-fa">{{ transaction?.seller?.mobile || '—' }}</span>
            </DetailRow>
            <DetailRow label="ایمیل">
              <span>{{ transaction?.seller?.email || '—' }}</span>
            </DetailRow>
            <DetailRow label="تعداد تراکنش های موفق">
              <span class="font-fa">{{ transaction?.seller?.succeedTransaction }}</span>
            </DetailRow>
            <DetailRow label="نوع انتقال">
              <span v-if="transaction?.requisition?.type === 'SELL'" class="font-fa">
                {{
                  transaction?.requisition?.transferMethods?.length > 0
                    ? transaction?.requisition?.transferMethods.map(method => method.label).join('، ')
                    : 'تعیین نشده'
                }}
              </span>
              <span v-if="transaction?.requisition?.type === 'BUY'" class="font-fa">
                {{
                  transaction?.offer?.transferMethods?.length > 0
                    ? transaction?.offer?.transferMethods.map(method => method.label).join('، ')
                    : 'تعیین نشده'
                }}
              </span>
            </DetailRow>
            <DetailRow label="کشور مبداء حساب ارزی">
              <span>{{ transaction?.bankAccountCountries?.seller?.title?.fa || '—' }}</span>
            </DetailRow>
          </div>

          <!-- اطلاعات مالی فروشنده -->
          <div class="flex flex-col gap-4">
            <h3 class="text-sm font-semibold text-[#303b4f]">اطلاعات مالی فروشنده</h3>

            <DetailRow label="مبلغ تسویه">
              <span class="font-fa">{{ numberWithCommas(transaction?.sellerReceivableRialAmount) }} تومان</span>
            </DetailRow>
            <DetailRow label="کارمزد">
              <span class="font-fa">
                {{ transaction?.sellerCommissionDiscountedRialAmount ?
                  numberWithCommas(transaction?.sellerCommissionDiscountedRialAmount) : 0 }} تومان
              </span>
            </DetailRow>
            <DetailRow label="مالیات">
              <span class="font-fa">
                {{ transaction?.sellerTaxRialAmount ? numberWithCommas(transaction?.sellerTaxRialAmount) : 0 }} تومان
              </span>
            </DetailRow>
            <DetailRow label="تخفیف">
              <span>{{ transaction?.discount?.seller ? 'دارد' : 'ندارد' }}</span>
            </DetailRow>
            <DetailRow v-if="transaction?.discount?.seller" label="عنوان تخفیف">
              <span>{{ transaction?.discount?.seller?.title || '—' }}</span>
            </DetailRow>
            <DetailRow v-if="transaction?.discount?.seller" label="شماره تخفیف">
              <span class="font-fa">{{ transaction?.discount?.seller?.id }}</span>
            </DetailRow>
            <DetailRow v-if="transaction?.discount?.seller" label="درصد تخفیف">
              <span class="font-fa">{{ transaction?.discount?.seller?.rate }}%</span>
            </DetailRow>
          </div>
        </div>

        <!-- حساب فروشنده -->
        <div class="mt-8 border-t border-[#eaeef5] pt-6">
          <h3 class="text-sm font-semibold text-[#303b4f] mb-4">اطلاعات حساب فروشنده</h3>

          <div class="grid lg:grid-cols-2 gap-x-8 gap-y-3">
            <DetailRow label="نام دارنده حساب">
              <span>{{ transaction?.bankAccounts?.sellerBank?.ownerFullName || '—' }}</span>
            </DetailRow>
            <DetailRow label="نام بانک">
              <span>{{ transaction?.bankAccounts?.sellerBank?.bankName || '—' }}</span>
            </DetailRow>
            <DetailRow label="شماره شبا">
              <span class="font-fa" dir="ltr">{{ transaction?.bankAccounts?.sellerBank?.shebaNumber || '—' }}</span>
            </DetailRow>
            <DetailRow label="شماره کارت">
              <span class="font-fa" dir="ltr">{{ transaction?.bankAccounts?.sellerBank?.cardNumber || '—' }}</span>
            </DetailRow>
          </div>
        </div>
      </div>
      </div>
    </section>


    <!-- RECEIPTS (TABS) -->
    <section class="mt-10">
      <div class="mb-4 inline-flex items-center gap-1 rounded-xl border border-[#d8d8d8] bg-white p-1">
        <button type="button" @click="activeReceiptTab = 'buyerReceipt'"
          class="flex items-center gap-2 rounded-lg px-6 py-2 text-sm font-semibold transition-colors"
          :class="activeReceiptTab === 'buyerReceipt' ? 'bg-[#2d5396] text-white' : 'text-[#4e648c] hover:bg-[#f2f5fa]'">
          رسیدهای خریدار
        </button>
        <button type="button" @click="activeReceiptTab = 'sellerReceipt'"
          class="flex items-center gap-2 rounded-lg px-6 py-2 text-sm font-semibold transition-colors"
          :class="activeReceiptTab === 'sellerReceipt' ? 'bg-[#2d5396] text-white' : 'text-[#4e648c] hover:bg-[#f2f5fa]'">
          رسیدهای فروشنده
        </button>
        <button type="button" @click="activeReceiptTab = 'settlementReceipt'"
          class="flex items-center gap-2 rounded-lg px-6 py-2 text-sm font-semibold transition-colors"
          :class="activeReceiptTab === 'settlementReceipt' ? 'bg-[#2d5396] text-white' : 'text-[#4e648c] hover:bg-[#f2f5fa]'">
          رسیدهای تسویه
        </button>
      </div>

      <!-- Buyer receipt tab -->
      <div v-show="activeReceiptTab === 'buyerReceipt'">
        <div class="flex flex-col gap-4 h-full" v-if="!!transaction">
          <!-- <div class="text-[#1b325a] text-[18px]">
            تصاویر پرداخت‌های خریدار
          </div> -->
          <TransactionFileUpload label="خریدار" type="iranian" :disabled="transaction?.status === 'TRANSFERRED'"
            :uuid="transaction!.uuid!.toString()" :img="transaction?.receipts!.iranian.flatMap(x => x?.toString())"
            @confirmUpload="reloadData" />
        </div>
      </div>

      <!-- Seller receipt tab -->
      <div v-show="activeReceiptTab === 'sellerReceipt'">
        <div class="flex flex-col gap-4 h-full">
          <!-- <div class="text-[#1b325a] text-[18px]">
            تصاویر پرداخت‌های فروشنده
          </div> -->
          <TransactionFileUpload label="فروشنده " type="international" :disabled="transaction?.status === 'TRANSFERRED'"
            :uuid="transaction?.uuid as string" :img="transaction?.receipts?.international as string[]"
            @confirmUpload="reloadData" />
        </div>
      </div>

      <!-- Settlement receipt tab -->
      <div v-show="activeReceiptTab === 'settlementReceipt'">
        <div class="flex flex-col gap-4 h-full">
          <!-- <div class="text-[#1b325a] text-[18px]">
            تصاویر تسویه با فروشنده
          </div> -->
          <TransactionFileUpload label="خریدار" type="settlement" :disabled="transaction?.status === 'TRANSFERRED'"
            :uuid="transaction?.uuid as string" :img="transaction?.receipts?.sellerSettlement?.images as string[]"
            @confirmUpload="reloadData" />
        </div>
      </div>
    </section>


    <TransactionNotes ref="notesRef" :transactionId="transactionId" />

    <TransactionTransferHistory v-if="transactionTransfersResponse?.data?.length"
      :transfers="transactionTransfersResponse?.data ?? []" />



  </div>
</template>
